import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import HomeSearchBar from "@/pages/home/components/HomeSearchBar";
import SectionHeading from "@/components/feature/SectionHeading";
import FilmCard from "@/components/feature/FilmCard";
import SpecialCard from "@/components/feature/SpecialCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { films } from "@/mocks/films";
import { specials } from "@/mocks/specials";
import { getFilmsByType, getHotFilms, getLatestFilms } from "@/utils/film";

const categories = [
  {
    type: "电影",
    to: "/category/电影",
    icon: "ri-movie-2-line",
    desc: "妖精题材长篇电影，剧情片与动画佳作一网打尽",
  },
  {
    type: "电视剧",
    to: "/category/电视剧",
    icon: "ri-tv-2-line",
    desc: "妖精题材连续剧，追剧党不可错过的连载精选",
  },
];

export default function Home() {
  useDocumentMeta(
    "妖精网站-妖精题材影视作品大全与剧情介绍",
    "妖精网站专注收录妖精主题的电影、电视剧资源，提供丰富的剧情介绍和作品推荐，满足喜爱妖精题材影视内容用户的观影需求。"
  );

  const latestFilms = getLatestFilms(10);
  const hotFilms = getHotFilms(8);

  return (
    <div className="w-full">
      <HeroCarousel />
      <HomeSearchBar />

      {/* 分类快捷入口 */}
      <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <SectionHeading
          title="按类型探索"
          subtitle="快速进入你感兴趣的分类，浏览完整的妖精影视资源"
          icon="ri-compass-3-line"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6">
          {categories.map((category) => (
            <Link
              key={category.type}
              to={category.to}
              className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-secondary-300 md:p-6"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-secondary-500 text-background-50 transition-colors group-hover:bg-accent-500 group-hover:text-primary-950 md:h-16 md:w-16">
                <i className={`${category.icon} text-2xl md:text-3xl`} />
              </span>
              <div className="flex-1">
                <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  妖精{category.type}
                  <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-700">
                    {getFilmsByType(category.type).length} 部
                  </span>
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">{category.desc}</p>
              </div>
              <i className="ri-arrow-right-s-line text-2xl text-foreground-400 transition-all group-hover:translate-x-1 group-hover:text-primary-600" />
            </Link>
          ))}
        </div>
      </section>

      {/* 最新发布 */}
      <section className="w-full bg-background-100/70">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <SectionHeading
            title="最新发布"
            subtitle="近期上线的妖精影视作品，第一时间掌握更新动态"
            icon="ri-time-line"
            moreTo="/category/电视剧"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
            {latestFilms.map((film) => (
              <FilmCard key={film.id} film={film} showIntro={false} />
            ))}
          </div>
        </div>
      </section>

      {/* 热门推荐 */}
      <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <SectionHeading
          title="热门推荐"
          subtitle="根据观众热度精选，最受欢迎的妖精影视作品"
          icon="ri-fire-line"
          moreTo="/category/电影"
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {hotFilms.map((film) => (
            <FilmCard key={film.id} film={film} showPopularity showIntro={false} />
          ))}
        </div>
      </section>

      {/* 专题精选 */}
      <section className="w-full bg-background-100/70">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <SectionHeading
            title="专题精选"
            subtitle="由编辑精心策划的妖精主题合集，带你系统探索妖精世界"
            icon="ri-bookmark-3-line"
            moreTo="/special/s1"
            moreLabel="浏览更多专题"
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
            {specials.map((special) => (
              <SpecialCard key={special.id} special={special} filmCount={special.filmIds.length} />
            ))}
          </div>
        </div>
      </section>

      {/* 网站简介 */}
      <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 items-center gap-8 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-6 md:grid-cols-2 md:p-10">
          <div>
            <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-information-line" />
              关于妖精网站
            </span>
            <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              妖精题材影视的综合门户
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              妖精网站专注收录妖精主题的电影、电视剧资源，提供丰富的剧情介绍和作品推荐。无论你想寻找一部治愈的森林精灵物语，还是沉浸在史诗级的精灵王国之战，都能在这里便捷地找到心仪的作品。
            </p>
            <div className="mt-6 flex flex-wrap gap-6">
              <div>
                <p className="font-heading text-2xl font-bold text-primary-600">{films.length}</p>
                <p className="text-xs text-foreground-500">收录作品</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-accent-600">
                  {specials.length}
                </p>
                <p className="text-xs text-foreground-500">精选专题</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-secondary-600">100%</p>
                <p className="text-xs text-foreground-500">妖精题材</p>
              </div>
            </div>
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              了解更多
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-72">
            <img
              src="https://readdy.ai/api/search-image?query=Artistic%20illustration%20of%20a%20mystical%20enchanted%20forest%20clearing%20with%20several%20small%20glowing%20fairies%20and%20spirits%20gathered%20around%20a%20magical%20glowing%20tree%2C%20soft%20lavender%20and%20warm%20amber%20light%2C%20dreamy%20storybook%20style%2C%20high%20detail&width=1000&height=700&seq=fairy-about-home&orientation=landscape"
              alt="妖精网站品牌插画"
              title="妖精网站 妖精题材影视门户"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>
    </div>
  );
}